import { useState, useEffect, useCallback, memo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Plus, Trash2, History, RotateCcw, BarChart3, Cloud } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const fmt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const SheetRow = memo(function SheetRow({ row, ri, ncols, isHeader, canEdit, onCell, onDelete }) {
  return (
    <tr className={`border-b border-slate-100 group ${isHeader ? "bg-slate-100 font-semibold text-slate-700" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`sheet-row-${ri}`}>
      {Array.from({ length: ncols }).map((_, ci) => (
        <td key={ci} className={`px-0.5 py-0.5 ${ci === 0 ? "sticky left-0 z-10 " + (isHeader ? "bg-slate-100" : "bg-white group-hover:bg-slate-50") : ""}`}>
          {canEdit ? (
            <input
              value={row.cells[ci] ?? ""}
              onChange={(e) => onCell(ri, ci, e.target.value)}
              className={`h-7 ${ci === 0 ? "w-44 text-left font-medium px-2" : "w-[84px] text-right font-mono px-1.5"} text-xs rounded border border-transparent hover:border-slate-200 focus:border-brand focus:bg-white bg-transparent outline-none transition-colors`}
              data-testid={`sheet-cell-${ri}-${ci}`}
            />
          ) : (
            <span className={`block px-1.5 text-xs ${ci === 0 ? "text-left font-medium" : "text-right font-mono"}`}>{row.cells[ci] ?? ""}</span>
          )}
        </td>
      ))}
      {canEdit && (
        <td className="px-1 sticky right-0 bg-inherit">
          <button type="button" title="Delete row" onClick={() => onDelete(ri)}
            className="h-6 w-6 rounded flex items-center justify-center text-slate-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`sheet-row-delete-${ri}`}><Trash2 className="h-3 w-3" /></button>
        </td>
      )}
    </tr>
  );
});

function SheetVersions({ slug, open, onOpenChange, canRestore, onRestored }) {
  const { data, isLoading } = useQuery({
    queryKey: ["sheet-versions", slug],
    queryFn: async () => (await api.get(`/sales-mis/edit-sheets/${slug}/versions`)).data,
    enabled: open && canRestore,
  });
  const [restoring, setRestoring] = useState("");
  const restore = async (id) => {
    setRestoring(id);
    try {
      await api.post(`/sales-mis/edit-sheets/${slug}/versions/${id}/restore`);
      toast.success("Sheet restored from this version.");
      onRestored();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setRestoring(""); }
  };
  const versions = data || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sheet-versions-dialog">
        <DialogHeader><DialogTitle>Version history</DialogTitle>
          <DialogDescription className="text-xs text-slate-400">A checkpoint is kept every 4 hours of edits. Restoring auto-saves the current state first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-2">
          {isLoading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            : versions.length === 0 ? <p className="text-center py-10 text-sm text-slate-400">No versions yet.</p>
            : versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`sheet-version-${v.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{new Date(v.saved_at).toLocaleString()}</p>
                  <p className="text-xs text-slate-400 truncate">{v.note} · by {v.saved_by}</p>
                </div>
                <Button size="sm" variant="outline" disabled={!!restoring} onClick={() => restore(v.id)} className="gap-1.5 shrink-0" data-testid={`sheet-version-restore-${v.id}`}>
                  {restoring === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                </Button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditableSheetGrid({ slug }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["edit-sheet", slug],
    queryFn: async () => (await api.get(`/sales-mis/edit-sheets/${slug}`)).data,
    refetchInterval: (q) => (q.state.data?.sharepoint?.locked ? 120000 : false),
  });
  const sp = data?.sharepoint;
  const canEdit = !!data?.can_edit;
  const canRestore = !!data?.can_restore;
  const dashboard = data?.dashboard || [];

  const [title, setTitle] = useState("");
  const [rows, setRows] = useState([]);
  const [ncols, setNcols] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  const hydrate = useCallback((s) => {
    setTitle(s?.title || "");
    const rws = (s?.rows || []).map((r) => ({ id: r.id, cells: [...(r.cells || [])] }));
    setRows(rws);
    setNcols(s?.ncols || Math.max(0, ...rws.map((r) => r.cells.length)));
    setDirty(false);
  }, []);

  useEffect(() => { if (data?.sheet) hydrate(data.sheet); }, [data?.sheet, hydrate]);

  const onCell = useCallback((ri, ci, val) => {
    setRows((prev) => {
      const next = [...prev];
      const cells = [...next[ri].cells];
      while (cells.length <= ci) cells.push("");
      cells[ci] = val;
      next[ri] = { ...next[ri], cells };
      return next;
    });
    setDirty(true);
  }, []);

  const onDelete = useCallback((ri) => { setRows((p) => p.filter((_, i) => i !== ri)); setDirty(true); }, []);
  const addRow = () => { setRows((p) => [...p, { id: crypto.randomUUID(), cells: Array(ncols || 1).fill("") }]); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put(`/sales-mis/edit-sheets/${slug}`, {
        title, rows: rows.map((r) => ({ id: r.id, cells: r.cells })),
      });
      hydrate(res.sheet);
      qc.invalidateQueries({ queryKey: ["edit-sheet", slug] });
      qc.invalidateQueries({ queryKey: ["sheet-versions", slug] });
      toast.success("Sheet saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="editable-sheet-grid">
      {sp?.locked && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 mb-4" data-testid="sharepoint-live-banner">
          <Cloud className="h-4 w-4 text-sky-600 shrink-0" />
          <p className="text-xs font-medium text-sky-800">
            Live from SharePoint · last synced {sp.last_sync_at ? new Date(sp.last_sync_at).toLocaleString() : "—"}
          </p>
          {sp.status === "error" && <p className="text-[11px] text-amber-700">· latest refresh failed — showing last good data</p>}
          <span className="ml-auto text-[10px] uppercase tracking-wide bg-sky-100 text-sky-700 rounded px-1.5 py-0.5 font-semibold">Read-only</span>
        </div>
      )}
      {dashboard.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="sheet-dashboard">
          {dashboard.map((c, i) => (
            <Card key={i} className="p-3" data-testid={`sheet-kpi-${i}`}>
              <div className="flex items-center gap-1.5 text-slate-400 mb-1"><BarChart3 className="h-3.5 w-3.5" /><span className="text-[10px] uppercase tracking-wide truncate" title={c.label}>{c.label}</span></div>
              <p className="font-heading text-lg font-bold text-slate-900 leading-none">{fmt(c.value)}</p>
            </Card>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs text-slate-400">{rows.length} rows{data?.sheet?.updated_at ? ` · last edited ${new Date(data.sheet.updated_at).toLocaleString()}${data.sheet.updated_by ? ` by ${data.sheet.updated_by}` : ""}` : ""}{canEdit ? "" : " · read-only"}</p>
        <div className="flex items-center gap-2">
          {canRestore && <Button variant="outline" onClick={() => setShowVersions(true)} className="gap-1.5" data-testid="sheet-history-btn"><History className="h-4 w-4" /> History</Button>}
          {canEdit && <>
            <Button variant="outline" onClick={addRow} className="gap-1.5" data-testid="sheet-add-row-btn"><Plus className="h-4 w-4" /> Add row</Button>
            <Button onClick={save} disabled={!dirty || saving} className="bg-brand hover:bg-brand/90 text-white gap-1.5" data-testid="sheet-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving…" : "Save"}
            </Button>
          </>}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto max-h-[68vh]">
          <table className="border-collapse">
            <tbody>
              {rows.map((row, ri) => (
                <SheetRow key={row.id || ri} row={row} ri={ri} ncols={ncols} isHeader={ri === 0}
                  canEdit={canEdit} onCell={onCell} onDelete={onDelete} />
              ))}
              {rows.length === 0 && <tr><td className="text-center py-10 text-slate-400 text-sm">No rows. Click "Add row" to begin.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <SheetVersions slug={slug} open={showVersions} onOpenChange={setShowVersions} canRestore={canRestore}
        onRestored={() => { qc.invalidateQueries({ queryKey: ["edit-sheet", slug] }); qc.invalidateQueries({ queryKey: ["sheet-versions", slug] }); }} />
    </div>
  );
}
